import { motion } from "framer-motion";
import { Play, Pause, Loader2 } from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { TRACKS, TRACK_THEMES } from "../data/tracks";
import { useAudio } from "../context/AudioContext";

export default function ListenPage() {
  const { playTrack, currentId, isPlaying, loading } = useAudio();

  return (
    <PageShell>
      <section data-testid="listen-page" className="relative pt-36 md:pt-44 pb-14 overflow-hidden grain">
        <div className="aurora-weave" />
        <div className="line-weave" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 text-center">
          <p className="text-sm tracking-[0.24em] uppercase text-[#E7CC86]/80 mb-4">The Listening Room</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-cream mb-5">
            Sometimes the medicine is <span className="text-gold">simply to listen.</span>
          </h1>
          <p className="text-lg text-cream/70 max-w-2xl mx-auto">
            Press play and the music follows you across the site. Begin anywhere — with the theme that meets you where you are.
          </p>
        </div>
      </section>

      <section className="relative py-10 md:py-16 bg-[#0B1020]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 space-y-12">
          {TRACK_THEMES.map((theme) => {
            const items = TRACKS.filter((t) => t.theme === theme.id);
            if (!items.length) return null;
            return (
              <div key={theme.id} data-testid={`listen-theme-${theme.id}`}>
                <div className="flex items-center gap-4 mb-5">
                  <span className="font-display text-[#E7CC86]/60 text-lg tracking-widest">{theme.id}</span>
                  <h2 className="font-display text-xl md:text-2xl text-cream">{theme.title}</h2>
                  <span className="flex-1 h-px bg-[#C9A24B]/15" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {items.map((t, i) => {
                    const active = currentId === t.id;
                    const playingThis = active && isPlaying;
                    return (
                      <motion.button key={t.id} data-testid={`listen-track-${t.id}`} onClick={() => playTrack(t.id)}
                        initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                        className={`text-left glass rounded-2xl p-3.5 flex items-center gap-4 transition-all group ${active ? "border-[#C9A24B]/55" : "hover:border-[#C9A24B]/35"}`}>
                        <div className="relative w-14 h-14 shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-[#12203a] to-[#05070C]">
                          <img loading="lazy" src={t.cover} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity">
                            {playingThis ? <Pause size={20} className="text-[#E7CC86]" fill="#E7CC86" /> : <Play size={20} className="ml-0.5 text-[#E7CC86]" fill="#E7CC86" />}
                          </div>
                          {loading && active && <div className="absolute inset-0 flex items-center justify-center bg-black/40"><Loader2 size={18} className="animate-spin text-[#E7CC86]" /></div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-display text-lg truncate ${active ? "text-[#E7CC86]" : "text-cream"}`}>{t.title}</p>
                          <p className="text-sm text-cream/50 truncate">{t.feat}</p>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </PageShell>
  );
}
